// src/pages/RegistrarFraccionamiento.js
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useNotification } from '../context/NotificationContext'; 
import PlatformBackButton from '../components/PlatformBackButton';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import './RegistrarFraccionamiento.css';

const RegistrarFraccionamiento = () => {
    const location = useLocation();
    const navigate = useNavigate(); // PASO 2: Inicializa useNavigate
    const { showNotification } = useNotification(); // Inicializa las notificaciones

    // Obtenemos los datos iniciales si vienen desde la página de solicitudes
    const initialData = location.state?.initialData;

    const [formData, setFormData] = useState({
        nombreFracc: '',
        suscripcion: 'Basica',
        fechaVencimiento: '',
        nombreAdmin: '',
        emailAdmin: '',
        passwordAdmin: '',
    });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const prefilledRef = useRef(false);

    useEffect(() => {
        if (!initialData || prefilledRef.current) {
            return;
        }

        const prefillNombreFracc =
            initialData.nombreFracc ||
            initialData.fraccionamiento ||
            initialData.nombreFraccionamiento ||
            '';
        const prefillNombreAdmin =
            initialData.nombreAdmin ||
            initialData.adminPrincipal ||
            initialData.contacto ||
            initialData.nombre ||
            '';
        const prefillEmail = initialData.email || initialData.emailAdmin || '';

        setFormData((prev) => ({
            ...prev,
            nombreFracc: prefillNombreFracc || prev.nombreFracc,
            nombreAdmin: prefillNombreAdmin || prev.nombreAdmin,
            emailAdmin: prefillEmail || prev.emailAdmin,
        }));
        prefilledRef.current = true;
    }, [initialData]);

    const formattedSolicitud = useMemo(() => {
        if (!initialData) {
            return null;
        }

        const fechaRecibida = initialData.fecha?.toDate ? initialData.fecha.toDate().toLocaleString() : 'Sin registro';
        const actualizado = initialData.actualizado?.toDate ? initialData.actualizado.toDate().toLocaleString() : 'Sin registro';

        return {
            estado: (initialData.estado || 'pendiente').toLowerCase(),
            fechaRecibida,
            actualizado,
            numLotes: initialData.numLotes ?? 'No indicado',
            celular: initialData.celular || 'No indicado',
            comentarios: initialData.comentarios || null,
            email: initialData.email || '',
            origen: initialData.origen || 'Portal público',
            nombreFracc:
                initialData.nombreFracc ||
                initialData.fraccionamiento ||
                initialData.nombreFraccionamiento ||
                'No indicado',
            nombreAdmin:
                initialData.nombreAdmin ||
                initialData.adminPrincipal ||
                initialData.contacto ||
                initialData.nombre ||
                'No indicado',
        };
    }, [initialData]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.passwordAdmin.length < 6) {
            showNotification('La contraseña debe tener al menos 6 caracteres.', 'error');
            return;
        }
        setLoading(true);

        try {
            const dataToSubmit = {
                ...formData,
                nombreFracc: formData.nombreFracc || formattedSolicitud?.nombreFracc || '',
                nombreAdmin: formData.nombreAdmin || formattedSolicitud?.nombreAdmin || '',
                emailAdmin: formData.emailAdmin || formattedSolicitud?.email || '',
                solicitudId: initialData?.id || initialData?.solicitudId || null,
            };

            const crearNuevoFraccionamiento = httpsCallable(functions, 'crearNuevoFraccionamiento');
            const result = await crearNuevoFraccionamiento(dataToSubmit);

                // La función siempre retorna success:true si llegamos aquí
                showNotification(
                    result.data.message || '¡Fraccionamiento y administrador creados con éxito!',
                    'success'
                );
            
                // Redirigir a la lista de fraccionamientos
                navigate('/fraccionamientos');
            
        } catch (err) {
            console.error("Error al llamar a la Cloud Function:", err);
            
                // Extraer mensaje de error apropiado
                let errorMessage = 'Error al crear el fraccionamiento. Por favor intenta nuevamente.';
            
                if (err.code) {
                    // Es un HttpsError de Firebase Functions
                    switch (err.code) {
                        case 'unauthenticated':
                            errorMessage = 'Debes iniciar sesión para realizar esta acción.';
                            break;
                        case 'permission-denied':
                            errorMessage = err.message || 'No tienes permisos para crear fraccionamientos.';
                            break;
                        case 'invalid-argument':
                            errorMessage = err.message || 'Los datos proporcionados no son válidos.';
                            break;
                        case 'already-exists':
                            errorMessage = err.message || 'El correo electrónico ya está registrado.';
                            break;
                        default:
                            errorMessage = err.message || errorMessage;
                    }
                } else if (err.message) {
                    errorMessage = err.message;
                }
            
                showNotification(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="registrar-fracc-page">
        <div className="registrar-fracc-container">
            <div className="registrar-fracc-header">
                {/* PASO 4: Usa navigate para el botón de volver */}
                <PlatformBackButton />
                <div>
                    <h2>Registrar Nuevo Fraccionamiento</h2>
                    <p>Crea una nueva entidad de fraccionamiento y su cuenta de superadministrador.</p>
                </div>
            </div>
            <div className="registrar-fracc-body">
            <form onSubmit={handleSubmit} className="registrar-fracc-form">
                <h4>Datos del Fraccionamiento</h4>
                <div className="form-grid">
                    <div className="form-group">
                        <label>Nombre del Fraccionamiento</label>
                        <input
                            type="text"
                            name="nombreFracc"
                            value={formData.nombreFracc || formattedSolicitud?.nombreFracc || ''}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Plan de Suscripción</label>
                        <select name="suscripcion" value={formData.suscripcion} onChange={handleChange}>
                            <option value="Demo">Demo</option>
                            <option value="Basica">Basica</option>
                            <option value="Estándar">Estándar</option>
                            <option value="Premium">Premium</option>
                            <option value="Premium-Plus">Premium-Plus</option>
                        </select>
                    </div>
                </div>

                <div className="form-group">
                    <label>Fecha de Vencimiento de la Suscripción</label>
                    <input type="date" name="fechaVencimiento" value={formData.fechaVencimiento} onChange={handleChange} required />
                </div>

                <h4 className="form-divider">Datos del Administrador Principal</h4>
                <div className="form-group">
                    <label>Nombre Completo del Administrador</label>
                    <input
                        type="text"
                        name="nombreAdmin"
                        value={formData.nombreAdmin || formattedSolicitud?.nombreAdmin || ''}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className="form-group">
                    <label>Email del Administrador</label>
                    <input
                        type="email"
                        name="emailAdmin"
                        value={formData.emailAdmin || formattedSolicitud?.email || ''}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className="form-group password-group">
                    <label>Contraseña Temporal</label>
                    <div className="password-wrapper">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            name="passwordAdmin"
                            value={formData.passwordAdmin}
                            onChange={handleChange}
                            required
                        />
                        <button
                            type="button"
                            className="password-toggle"
                            onClick={() => setShowPassword((prev) => !prev)}
                            aria-label={showPassword ? 'Ocultar contraseña temporal' : 'Mostrar contraseña temporal'}
                        >
                            {showPassword ? <FiEyeOff /> : <FiEye />}
                        </button>
                    </div>
                    <small className="password-hint">Puedes copiar esta contraseña temporal para compartirla con el nuevo administrador.</small>
                </div>

                {/* El estado de 'error' ya no es necesario, usamos el sistema de notificaciones */}

                <div className="form-actions">
                    <button type="submit" className="btn-submit" disabled={loading}>
                        {loading ? 'Creando...' : 'Crear Registro'}
                    </button>
                </div>
            </form>
            {formattedSolicitud && (
                <aside className="solicitud-panel">
                    <h4>Solicitud recibida</h4>
                    <p className={`estado-pill estado-${formattedSolicitud.estado}`}>
                        Estado actual: <strong>{formattedSolicitud.estado}</strong>
                    </p>
                    <dl>
                        <div>
                            <dt>Fraccionamiento</dt>
                            <dd>{formattedSolicitud.nombreFracc}</dd>
                        </div>
                        <div>
                            <dt>Contacto</dt>
                            <dd>{formattedSolicitud.nombreAdmin}</dd>
                        </div>
                        <div>
                            <dt>Correo</dt>
                            <dd>{formattedSolicitud.email}</dd>
                        </div>
                        <div>
                            <dt>Teléfono</dt>
                            <dd>{formattedSolicitud.celular}</dd>
                        </div>
                        <div>
                            <dt>Lotes estimados</dt>
                            <dd>{formattedSolicitud.numLotes}</dd>
                        </div>
                        <div>
                            <dt>Origen</dt>
                            <dd>{formattedSolicitud.origen}</dd>
                        </div>
                        <div>
                            <dt>Recibido</dt>
                            <dd>{formattedSolicitud.fechaRecibida}</dd>
                        </div>
                        <div>
                            <dt>Última actualización</dt>
                            <dd>{formattedSolicitud.actualizado}</dd>
                        </div>
                        {formattedSolicitud.comentarios && (
                            <div>
                                <dt>Comentarios</dt>
                                <dd>{formattedSolicitud.comentarios}</dd>
                            </div>
                        )}
                    </dl>
                    <p className="panel-hint">Verifica esta información antes de finalizar el registro.</p>
                </aside>
            )}
            </div>
        </div>
        </div>
    );
};

export default RegistrarFraccionamiento;